<script>
  import { beforeUpdate, onMount } from "svelte";
  import { products } from "../models/products.js";
  import { formatPrice, formatDate } from "../services/utilities.js";
  import Popup from "./Popup.svelte";
  import { cart } from "../models/cart.js";
  import { subscription } from "../models/subscription.js";
  import { user } from "../models/user.js";
  import { changeSubscriptionPlan } from "../models/orders.js";
  import { track, planParams } from "../services/analytics.js";
  import { pendingPlan } from "../models/pendingPlan.js";

  let membershipContent = {
    "Casual Puzzler Plan": {
      img: "/assets/plan-img-1.png",
      desc: "Ideal for puzzlers that steadily complete a puzzle within 7 to 10 days.",
      steps: ["3 puzzles per month", "1 shipment per month"],
    },
    "Avid Puzzler Plan": {
      img: "/assets/plan-img-2.png",
      desc: "Ideal for puzzlers that steadily complete a puzzle within 3 to 6 days.",
      steps: ["6 puzzles per month", "2 shipments per month"],
      popular: true,
    },
    "Denver Puzzler Plan": {
      img: "/assets/plan-img-3.png",
      desc: "Ideal for puzzlers that live in the Denver, Colorado area.",
      steps: ["Unlimited puzzles per month", "Must pick up in store"],
    },
  };

  $: memberships = [];
  $: durationType = "month";

  export let changingPlan = false;
  export let hideLocalPlan = false;
  export let currentPlan = null;

  let username = null;
  let needsAuth = false;
  let errorMsg;
  let changePlanDetails;
  let selectedChangePlan = null;

  beforeUpdate(async () => {
    if (Object.keys(membershipContent).length === 3 && hideLocalPlan === true) {
      delete membershipContent["Denver Puzzler Plan"];
    }
  });

  onMount(async () => {
    memberships = await products.getSubscriptions();
    if (hideLocalPlan) {
      // filter out denver plans
      memberships = memberships.filter(
        (m) => m["title"].indexOf("Denver") === -1
      );
    }
    username = await user.getUsername();
  });

  let formatTitle = (title) => {
    return title.trim().replaceAll(" Plan", "");
  };

  function clearError() {
    errorMsg = null;
    needsAuth = false;
  }

  function resetDetails() {
    changePlanDetails = null;
    selectedChangePlan = null;
  }

  function addSubToCart(product) {
    if ($subscription.hasSubscription) {
      errorMsg =
        "You already have an active subscription plan. View your subscription details <a href='/account'>here</a>.";
    } else if (cart.hasMembershipInCart()) {
      errorMsg =
        "You already have a subscription plan in your cart. Please remove it first, if you would like to change the plan.";
    } else if (cart.hasPuzzlePack()) {
      errorMsg =
        "Please remove the puzzle pack and rentals from your cart, if you would like to sign up for a subscription plan.";
    } else {
      // update product id to be the correct id, and same with price
      // this is because the user can select the month or year plan
      if (durationType == "month") {
        product.sku = product.month_product_sku;
        product.price = product.monthly_price;
        product.id = product.monthly_id;
      } else {
        product.sku = product.year_product_sku;
        product.price = product.yearly_price;
        product.id = product.yearly_id;
      }

      track("plan_selected", {
        ...planParams(product, durationType),
        logged_in: !!username,
      });

      if (username) {
        cart.add(product);
        window.location = "/checkout";
      } else {
        // remember the plan so we can drop them into checkout after signup
        pendingPlan.save(product, durationType);
        errorMsg = `Create a free account or sign in to continue. We'll save your <b>${formatTitle(
          product.title
        )} - ${getPrettyDurationType()}</b> plan. No charge until checkout.`;
        needsAuth = true;
      }
    }
  }

  function getPrettyDurationType() {
    return durationType.charAt(0).toUpperCase() + durationType.slice(1) + "ly";
  }

  function getCurrentPlanTitle() {
    return currentPlan.membership_name.split("-")[0].trim();
  }

  function changePlan(product) {
    if (durationType == "month") {
      product.sku = product.month_product_sku;
      product.price = product.monthly_price;
      product.id = product.monthly_id;
    } else {
      product.sku = product.year_product_sku;
      product.price = product.yearly_price;
      product.id = product.yearly_id;
    }

    selectedChangePlan = product.sku;

    changePlanDetails = `
    <h3 class="h3">You're Almost There!</h3>
    <p> You're switching from <b>${currentPlan.membership_name}</b> to <b>${product.title} - ${getPrettyDurationType()}</b>.</p>

    <h4 class="h4">Your current plan:</h4>
    <ul>
        <li>Price: ${formatPrice(currentPlan.recurring_price)} per ${durationType}</li>
        <li>Puzzles: ${membershipContent[getCurrentPlanTitle()].steps[0]}</li>
        <li>Shipments: ${membershipContent[getCurrentPlanTitle()].steps[1]}</li>
    </ul>

    <h4 class="h4">Your new plan:</h4>
    <ul>
        <li>Price: ${formatPrice(product.price)} per ${durationType}</li>
        <li>Puzzles: ${membershipContent[product.title].steps[0]}</li>
        <li>Shipments: ${membershipContent[product.title].steps[1]}</li>
    </ul>

    <p>You will be charged ${formatPrice(product.price)} on your next billing date: ${formatDate(
      currentPlan.next_bill_date,
      { showTime: false, formatStr: "MMMM dd, yyyy" }
    )}. </p>
    <p><b>Would you like to confirm this change?</b></p>
    `;
  }

  async function confirmChangePlan() {
    errorMsg = null;
    let res = await changeSubscriptionPlan(selectedChangePlan);
    if (res.error) {
      errorMsg = `Failed to change subscription plan: ${res.error}`;
    } else {
      window.location = "/account";
    }
    resetDetails();
  }
</script>

{#if errorMsg}
  <Popup message={errorMsg} close={() => clearError()} {needsAuth} />
{/if}

{#if changePlanDetails}
  <Popup
    message={changePlanDetails}
    confirm={() => confirmChangePlan()}
    confirmText="Yes, change my plan"
    cancelText="Go back"
    cancel={() => resetDetails()}
    close={() => resetDetails()}
  />
{/if}

<div class="subscription-container">
  <section class="wtr-options">
    <!--Monthly/Yearly toggle-->
    <div class="wtr-duration-toggle">
      <div class="wtr-duration-toggle-inner">
        <div>
          <span class="duration">Monthly</span>
        </div>
        <label class="switch">
          <input
            type="checkbox"
            on:click={() =>
              durationType == "month"
                ? (durationType = "year")
                : (durationType = "month")}
          />
          <span class="slider round" />
        </label>
        <div>
          <span class="duration">Yearly</span>
        </div>
      </div>
      <span class="smaller-text">1 month free when you sign-up for year.</span>
    </div>

    <!--Subscription plans-->
    <div class="wtr-options-memberships">
      {#each memberships as opt}
        <div
          class="wtr-options-membership"
          class:popular={membershipContent[opt.title].popular}
        >
          {#if membershipContent[opt.title].popular}
            <div class="popular-header">
              <span>Most Popular</span>
            </div>
          {/if}
          <div class="wtr-options-membership-img">
            <img src={membershipContent[opt.title].img} alt={opt.title} />
          </div>
          <div class="membership-title-header">
            <p class="membership-title">{formatTitle(opt.title)}</p>
            <button
              class="btn membership-info"
              on:click={() => (changePlanDetails = opt.description)}
            >
              i
            </button>
          </div>

          {#if durationType == "month"}
            <div class="price">
              <p class="num">{formatPrice(opt.monthly_price, true)}</p>
              <span class="duration">/{durationType}</span>
            </div>
          {:else}
            <div class="yearly-savings">
              <div class="price">
                <p class="num">{formatPrice(opt.yearly_price, true)}</p>
                <span class="duration">/{durationType}</span>
              </div>
              <div class="savings">
                <p>
                  Save {formatPrice(
                    opt.monthly_price * 12 - opt.yearly_price,
                    true
                  )}
                </p>
              </div>
            </div>
          {/if}
          <p class="card-subtext">{membershipContent[opt.title].desc}</p>
          <div>
            {#each membershipContent[opt.title].steps as step}
              <div class="wtr-checkbox-steps">
                {#if membershipContent[opt.title].popular}
                  <img src="/assets/checkbox-circle-popular.svg" alt={step} />
                {:else}
                  <img src="/assets/checkbox-circle.svg" alt={step} />
                {/if}
                <span>{step}</span>
              </div>
            {/each}
          </div>
          {#if changingPlan}
            <!--- membership page -->
            <button
              class="btn background-grey"
              on:click|preventDefault={() => {
                changePlan(opt);
              }}
              disabled={(durationType == "month" &&
                currentPlan["membership_name"].indexOf(opt.title) > -1 &&
                currentPlan["bill_monthly"]) ||
                (durationType == "year" &&
                  currentPlan["membership_name"].indexOf(opt.title) > -1 &&
                  !currentPlan["bill_monthly"])}
            >
              <!-- the data isn't perfect for matching so we need to check the data in a hacky-ish way -->
              {#if durationType == "month" && currentPlan["membership_name"].indexOf(opt.title) > -1 && currentPlan["bill_monthly"]}
                Your Current Plan
              {:else if durationType == "year" && currentPlan["membership_name"].indexOf(opt.title) > -1 && !currentPlan["bill_monthly"]}
                Your Current Plan
              {:else}
                Choose New Plan
              {/if}
            </button>
          {:else}
            <!--- ways to rent page -->
            <button
              class="btn background-grey"
              on:click|preventDefault={() => {
                addSubToCart(opt);
              }}
            >
              Choose plan
            </button>
          {/if}
        </div>
      {/each}
    </div>
  </section>
</div>

<style type="text/less">
  img {
    max-width: 100%;
  }

  .container {
    margin: auto;
    display: grid;
  }

  .hero {
    width: 100%;
  }

  .page-header-title {
    font-weight: 500;
    line-height: 60px;
    letter-spacing: 2px;
    text-align: center;
    margin: 0 auto;
  }

  section {
    padding-bottom: 2rem;
    text-align: center;
  }

  .section-header-text {
    margin: 0 auto;
    text-align: center;
    width: 50%;
  }
  .section-content {
    text-align: center;
  }

  .section-title {
    font-size: 32px;
    font-weight: 400;
    line-height: 38px;
  }

  .pay-as-you-go-title {
    font-size: 28px;
    line-height: 32px;
  }

  .wtr-how-it-works-steps {
    width: 40%;
    margin: 0 auto;
    text-align: left;
    padding-top: 2rem;
  }

  @media (max-width: 1150px) {
    .page-header-title {
      font-size: 38px;
    }
    .wtr-header {
      height: 315px;
    }
    .membership-title {
      font-size: 30px;
      line-height: 32px;
    }
    .section-header-text {
      padding: 1rem;
      width: 90%;
    }

    button {
      width: 100%;
    }
    .wtr-how-it-works-steps {
      width: 90%;
      padding: 1rem;
    }

    .wtr-options {
      .wtr-options-memberships {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        margin: 0 auto;
        justify-items: center;
        grid-gap: 2rem;
        justify-content: center;
        padding-top: 1rem;
      }

      .wtr-options-membership-img {
        img {
          width: 100px;
          height: 100px;
        }
      }
    }
    .wtr-options-pp {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      justify-items: center;
      align-items: center;
      margin: 0 auto;
      padding: 0;
      padding-bottom: 2rem;

      .card-img {
        text-align: center;
      }
    }

    .wtr-options-pp-card {
      width: 85%;
    }

    .wtr-gift {
      padding: 2rem;
    }

    .buy-section {
      flex-direction: column-reverse;
      width: 85%;

      .details {
        width: 100%;
      }
    }

    .wtr-btn {
      width: 100%;
    }

    .wtr-how-it-works-step {
      padding-bottom: 1rem;
    }
  }

  /* non mobile specific css */
  @media (min-width: 1150px) {
    .page-header-title {
      font-size: 48px;
    }
    .wtr-header {
      height: 250px;
      .hero-img {
        top: 200px;
      }
    }
    .membership-title {
      font-size: 36px;
      line-height: 60px;
    }
    .wtr-options {
      .wtr-options-memberships {
        padding-top: 1rem;
        display: flex;
        justify-content: center;
        margin: 0 auto;
        column-gap: 2rem;
      }

      .wtr-options-membership-img {
        img {
          width: 150px;
          height: 150px;
        }
      }
    }
    .wtr-options-pp {
      display: flex;
      justify-content: space-evenly;
      margin: 0 auto 3rem auto;
    }

    .buy-section {
      width: 75%;
      .details {
        width: 50%;
      }
    }

    .wtr-btn {
      width: 200px;
    }

    .wtr-how-it-works-step {
      padding-bottom: 2rem;
    }
  }

  .wtr-how-it-works-step {
    display: flex;
    column-gap: 2rem;

    img {
      width: 50px;
      height: 50px;
    }
  }

  .wtr-header {
    color: var(--col-accent-2);
    background-color: var(--col-accent-1-xl);

    .hero-img {
      position: absolute;

      img {
        width: 200px;
      }
    }
  }

  .wtr-options {
    .wtr-options-memberships {
      .popular {
        background-color: var(--col-accent-2-d);
        color: white;

        button {
          background-color: var(--col-accent-1);
        }

        .price {
          .num {
            color: white;
          }
        }
      }

      .popular-header {
        background-color: var(--col-accent-2-l2);
        margin-top: -33px;
        margin-left: -16px;
        padding: 8px 24px 8px 24px;
        width: 284px;
        border-radius: 16px 16px 0 0;
        text-align: center;
        text-transform: uppercase;
        font-size: 12px;
      }
    }

    .wtr-options-membership {
      padding: 1rem;
      background-color: white;
      width: 300px;
      border-radius: 16px;
      text-align: left;
      box-shadow: 0px 10px 5px 0px rgba(154, 160, 185, 0.05);
      box-shadow: 0px 15px 40px 0px rgba(166, 173, 201, 0.2);
      z-index: 100;

      button {
        width: 100%;
      }

      .price {
        display: flex;
        height: 50px;

        .num {
          color: var(--col-accent-2);
          font-size: 28px;
          line-height: 32px;
          margin-right: 5px;
        }
        .duration {
          font-size: 16px;
          line-height: 60px;
        }
      }
    }

    .wtr-options-membership-img {
      img {
        border-radius: 99px;
        border: 3px solid var(--col-accent-1);
      }

      margin: 15px auto;
      text-align: center;
    }
  }

  .wtr-btn {
    font-weight: 500;
    line-height: 20px;
    background-color: var(--col-gray-95);
    color: black;
    border: none;
    height: 44px;
  }

  .card-subtext {
    padding: 5px 0;
  }

  .wtr-options-pp {
    .card-img {
      height: 275px;

      img {
        height: 250px;
      }
    }

    .wtr-options-pp-card {
      padding: 1rem;
      background-color: white;
      text-align: left;
      padding-bottom: 2rem;
    }
  }

  .wtr-checkbox-steps {
    padding-bottom: 15px;
  }

  .buy-section {
    border: 1px var(--col-gray-90) solid;
    border-radius: 8px;
    padding: 1rem;
    margin: 0 auto;
    text-align: left;
    display: flex;
    justify-content: space-between;

    .details {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }
  .pay-as-you-go {
    margin: -3rem auto 0 auto;
    width: 100%;

    .price {
      color: var(--col-accent-2);
      font-size: 20px;
      line-height: 24px;
      display: inline;
    }
  }

  .background-white {
    background-color: hsla(0, 0%, 100%, 1);
  }

  .background-grey {
    background-color: var(--col-gray-95);
    color: black;
    border: none;
    height: 44px;
  }

  .yearly-savings {
    display: flex;
    align-items: center;
  }

  .savings {
    font-size: 12px;
    font-weight: 500;
    color: var(--col-success-d);
    border: 1px solid var(--col-success-xl);
    background-color: var(--col-success-xl);
    border-radius: 99px;
    padding: 4px 8px 4px 8px;
    height: 22px;
    width: 66px;
    text-align: center;
    line-height: 0px;
    margin-left: 10px;
  }

  .wtr-duration-toggle {
    padding-top: 1rem;
    padding-bottom: 2rem;

    .wtr-duration-toggle-inner {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .duration {
      font-size: 18px;
      font-weight: 500;
      vertical-align: middle;
      margin-right: 10px;
      margin-left: 10px;
    }
  }

  .smaller-text {
    font-size: 14px;
  }

  /* The switch - the box around the slider */
  .switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
  }

  /* Hide default HTML checkbox */
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  /* The slider */
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--col-accent-2);
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }

  input:checked + .slider {
    background-color: var(--col-accent-2);
  }

  input:focus + .slider {
    box-shadow: 0 0 1px var(--col-accent-2);
  }

  input:checked + .slider:before {
    -webkit-transform: translateX(26px);
    -ms-transform: translateX(26px);
    transform: translateX(26px);
  }

  /* Rounded sliders */
  .slider.round {
    border-radius: 34px;
  }

  .slider.round:before {
    border-radius: 50%;
  }

  .membership-title-header {
    display: flex;
  }

  .membership-title-header {
    .membership-info {
      height: 25px;
      width: 25px !important;
      border-radius: 50%;
      display: inline-block;
      text-align: center;
      border: solid 1px;
      align-self: center;
      margin-left: 15px;
      padding: 0;
      font-size: 15px;
    }
  }
</style>
